import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet } from "react-router-dom";
import { fetchMe, selectAuth } from "../features/auth/authSlice";
import { Sidebar } from "../components/Sidebar";
import { AppHeader } from "../components/AppHeader";
import {
  MobileDrawer,
  MobileTabBar,
  MobileTopBar,
} from "../components/MobileShell";
import { selectTheme, setTheme } from "../features/ui/uiSlice";
import { useIsMobile } from "../shared/useMediaQuery";
import AssistantWidget from "../features/assistant/AssistantWidget";
import { isEmbedMode } from "../shared/embedMode";
import "./AuthenticatedLayout.css";

export default function AuthenticatedLayout() {
  const dispatch = useDispatch();
  const auth = useSelector(selectAuth);
  const theme = useSelector(selectTheme);
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Не состояние: параметр задан адресом рамки и внутри неё не меняется.
  const embed = isEmbedMode();

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  useEffect(() => {
    if (!auth.user && auth.status === "idle") {
      dispatch(fetchMe());
    }
  }, [auth.status, auth.user, dispatch]);

  // Сервер — источник истины. Если у пользователя тема не задана, оставляем
  // текущую (её уже подставил localStorage-кэш из public/index.html),
  // иначе перезаписали бы осознанный выбор дефолтом.
  useEffect(() => {
    if (!auth.user) return;
    if (!auth.user.theme) return;
    dispatch(setTheme(auth.user.theme));
  }, [auth.user, dispatch]);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  // Уходим с мобильной ширины — drawer больше не нужен.
  useEffect(() => {
    if (!isMobile) setDrawerOpen(false);
  }, [isMobile]);

  // Предпросмотр в чате: только сама страница. Ни сайдбара, ни шапки,
  // ни виджета ассистента — иначе в рамке 260px видно всё, кроме того,
  // ради чего предпросмотр открывали. Проверяется до мобильной ветки:
  // ширина рамки мала и включила бы мобильный шелл с таб-баром.
  if (embed) {
    return (
      <div className="app-shell is-embed">
        <main className="app-content">
          <Outlet />
        </main>
      </div>
    );
  }

  // Мобильная ветка: сайдбар заменён верхней панелью и таб-баром.
  if (isMobile) {
    return (
      <div className="app-shell is-mobile">
        <MobileTopBar onOpenMenu={openDrawer} />
        <main className="app-content">
          <Outlet />
        </main>
        <MobileTabBar onOpenMenu={openDrawer} />
        <MobileDrawer open={drawerOpen} onClose={closeDrawer} />
        <AssistantWidget />
      </div>
    );
  }

  // Десктоп: сайдбар слева, над контентом — полоса с пользователем.
  // Блок пользователя переехал сюда из подвала сайдбара (AUFB-67).
  return (
    <div className="app-shell">
      <Sidebar />
      <div className="app-main">
        <AppHeader />
        <main className="app-content">
          <Outlet />
        </main>
      </div>
      <AssistantWidget />
    </div>
  );
}
